// ============================================================
// Page Devis — Lot L12 du plan SaaS (frontend)
// ============================================================
// Devis client avec validité 15j par défaut. Workflow :
//   en_cours → transforme (vente ou commande_client) | annule | expire
// ============================================================

import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import InputMontant from '../../components/common/InputMontant';
import { getDeviseCourante } from '../../utils/format';
import {
  FileText, Plus, X, Trash2, Loader, ShoppingCart, Package,
  Ban, Search, Eye, Calendar,
} from 'lucide-react';
import toast from 'react-hot-toast';

const STATUTS = {
  en_cours:    { label: 'En cours',     color: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  transforme:  { label: 'Transformé',   color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  annule:      { label: 'Annulé',       color: 'bg-gray-500/15 text-gray-400 border-gray-600/30' },
  expire:      { label: 'Expiré',       color: 'bg-orange-500/15 text-orange-300 border-orange-500/30' },
};

function fmt(n) { return Number(n || 0).toLocaleString('fr-FR') + ' ' + getDeviseCourante(); }
function fmtDate(iso) { return iso ? new Date(iso).toLocaleDateString('fr-FR') : '—'; }

// ─── Modale création devis ──────────────────────────────────
function ModalCreer({ onClose, onSuccess }) {
  const [variantes, setVariantes] = useState([]);
  const [form, setForm] = useState({
    client_nom: '', client_telephone: '', notes: '',
    validite_jours: 15,
    lignes: [{ variante_id: '', quantite: 1, prix_unitaire: 0 }],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/produits/variantes').then((r) => setVariantes(r.data || [])).catch(() => {});
  }, []);

  // Nouvel article ajouté EN TÊTE → le dernier saisi reste toujours en haut.
  const addLigne = () => setForm((f) => ({
    ...f, lignes: [{ variante_id: '', quantite: 1, prix_unitaire: 0 }, ...f.lignes],
  }));
  const setLigne = (i, k, v) => setForm((f) => ({
    ...f, lignes: f.lignes.map((l, idx) => idx === i ? { ...l, [k]: v } : l),
  }));
  const delLigne = (i) => setForm((f) => ({
    ...f, lignes: f.lignes.filter((_, idx) => idx !== i),
  }));

  const total = form.lignes.reduce((s, l) =>
    s + (parseInt(l.quantite) || 0) * (parseInt(l.prix_unitaire) || 0), 0);

  const submit = async () => {
    if (!form.client_nom.trim()) { toast.error('Nom client requis'); return; }
    const lignesValides = form.lignes.filter(l => l.variante_id && l.quantite > 0 && l.prix_unitaire >= 0);
    if (lignesValides.length === 0) { toast.error('Au moins une ligne valide requise'); return; }
    setSaving(true);
    try {
      await api.post('/devis', {
        client_nom: form.client_nom.trim(),
        client_telephone: form.client_telephone.trim() || null,
        notes: form.notes.trim() || null,
        validite_jours: parseInt(form.validite_jours) || 15,
        lignes: lignesValides.map(l => ({
          variante_id: l.variante_id,
          quantite: parseInt(l.quantite),
          prix_unitaire: parseInt(l.prix_unitaire),
        })),
      });
      toast.success('Devis créé');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Échec création');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl max-w-3xl w-full p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <FileText size={18} /> Nouveau devis
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Client *</label>
            <input type="text" value={form.client_nom}
              onChange={(e) => setForm({ ...form, client_nom: e.target.value })}
              className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white" />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Téléphone</label>
            <input type="text" value={form.client_telephone}
              onChange={(e) => setForm({ ...form, client_telephone: e.target.value })}
              className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white" />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Validité (jours)</label>
            <input type="number" min="1" max="90" value={form.validite_jours}
              onChange={(e) => setForm({ ...form, validite_jours: e.target.value })}
              className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white" />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Notes</label>
            <input type="text" value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white" />
          </div>
        </div>

        <div className="border border-gray-800 rounded-lg p-3 mb-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-300">Lignes du devis</p>
            <button onClick={addLigne} className="text-xs text-yellow-400 hover:text-yellow-300 flex items-center gap-1">
              <Plus size={12} /> Ajouter
            </button>
          </div>
          {form.lignes.map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-center">
              <select value={l.variante_id} onChange={(e) => setLigne(i, 'variante_id', e.target.value)}
                className="col-span-6 bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white">
                <option value="">— Choisir un article —</option>
                {variantes.map(v => (
                  <option key={v.id} value={v.id}>
                    {v.produit_nom} · {v.modele || v.code_variante}{v.couleur ? ` · ${v.couleur}` : ''}{v.taille_nom ? ` · ${v.taille_nom}` : ''}
                  </option>
                ))}
              </select>
              <input type="number" min="1" placeholder="Qté" value={l.quantite}
                onChange={(e) => setLigne(i, 'quantite', e.target.value)}
                className="col-span-2 bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white tabular-nums" />
              <InputMontant placeholder="Prix unit." value={l.prix_unitaire}
                onChange={(v) => setLigne(i, 'prix_unitaire', v)}
                className="col-span-3 bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white tabular-nums" />
              <button onClick={() => delLigne(i)} className="col-span-1 text-gray-500 hover:text-red-400">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          <div className="border-t border-gray-800 pt-2 mt-2 text-right">
            <span className="text-xs text-gray-400">Total : </span>
            <span className="text-lg font-bold text-yellow-400 tabular-nums">{fmt(total)}</span>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded-lg">Annuler</button>
          <button onClick={submit} disabled={saving}
            className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 disabled:opacity-40 text-gray-900 text-sm font-medium rounded-lg flex items-center gap-2">
            {saving ? <Loader size={14} className="animate-spin" /> : <FileText size={14} />} Créer le devis
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Modale détail / actions ─────────────────────────────────
function ModalDetail({ devisId, onClose, onChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/devis/${devisId}`).then(r => setData(r.data)).finally(() => setLoading(false));
  }, [devisId]);

  const action = async (verbe, label) => {
    if (!window.confirm(`${label} ce devis ?`)) return;
    try {
      if (verbe === 'annuler')                         await api.patch(`/devis/${devisId}/annuler`);
      else if (verbe === 'transformer-en-vente')       await api.post(`/devis/${devisId}/transformer-en-vente`);
      else if (verbe === 'transformer-en-commande')    await api.post(`/devis/${devisId}/transformer-en-commande`);
      toast.success('Action effectuée');
      onChange();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Échec');
    }
  };

  if (loading) return null;
  if (!data) return null;
  const { devis, lignes = [] } = data;
  const st = STATUTS[devis.statut] || STATUTS.en_cours;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-gray-900 border border-gray-700 rounded-xl max-w-2xl w-full p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-lg font-semibold text-white">Devis {devis.numero}</h3>
            <p className="text-xs text-gray-500">{devis.client_nom} {devis.client_telephone ? `· ${devis.client_telephone}` : ''}</p>
          </div>
          <span className={`px-2 py-1 rounded border text-xs ${st.color}`}>{st.label}</span>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm mb-4">
          <div><span className="text-gray-500">Émis :</span> {fmtDate(devis.date_emission)}</div>
          <div><span className="text-gray-500">Expire :</span> {fmtDate(devis.date_expiration)}</div>
        </div>

        <div className="border border-gray-800 rounded p-2 mb-4">
          <table className="w-full text-sm">
            <thead><tr className="text-xs text-gray-400">
              <th className="text-left p-1">Article</th>
              <th className="text-right p-1">Qté</th>
              <th className="text-right p-1">Prix unit.</th>
              <th className="text-right p-1">Total</th>
            </tr></thead>
            <tbody>{lignes.map((l, i) => (
              <tr key={i} className="border-t border-gray-800">
                <td className="p-1 text-gray-300">{l.produit_nom} {l.modele || l.code_variante}</td>
                <td className="p-1 text-right text-gray-400 tabular-nums">{l.quantite}</td>
                <td className="p-1 text-right text-gray-400 tabular-nums">{fmt(l.prix_unitaire)}</td>
                <td className="p-1 text-right text-white tabular-nums">{fmt(l.quantite * l.prix_unitaire)}</td>
              </tr>
            ))}</tbody>
            <tfoot><tr className="border-t border-gray-700">
              <td colSpan={3} className="p-1 text-right text-xs text-gray-400">Total</td>
              <td className="p-1 text-right font-bold text-yellow-400 tabular-nums">{fmt(devis.montant_total)}</td>
            </tr></tfoot>
          </table>
        </div>

        {devis.notes && <p className="text-sm text-gray-300 italic mb-3">"{devis.notes}"</p>}

        {(devis.statut === 'en_cours' || devis.statut === 'expire') && (
          <div className="flex flex-wrap justify-end gap-2 pt-3 border-t border-gray-800">
            <button onClick={() => action('annuler', 'Annuler')}
              className="px-3 py-1.5 bg-red-500/15 hover:bg-red-500/25 text-red-300 text-sm rounded flex items-center gap-1">
              <Ban size={14} /> Annuler
            </button>
            <button onClick={() => action('transformer-en-commande', 'Transformer en commande client')}
              className="px-3 py-1.5 bg-blue-500/15 hover:bg-blue-500/25 text-blue-300 text-sm rounded flex items-center gap-1">
              <Package size={14} /> → Commande
            </button>
            <button onClick={() => action('transformer-en-vente', 'Transformer en vente')}
              className="px-3 py-1.5 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 text-sm rounded flex items-center gap-1">
              <ShoppingCart size={14} /> → Vente
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function PageDevis() {
  const { canEditBusiness } = useAuth();
  const [devis, setDevis] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreer, setShowCreer] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [statutFiltre, setStatutFiltre] = useState('');
  const [recherche, setRecherche] = useState('');

  const charger = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statutFiltre) params.statut = statutFiltre;
      const { data } = await api.get('/devis', { params });
      setDevis(data.devis || data || []);
    } catch (err) {
      toast.error('Échec chargement');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { charger(); }, [statutFiltre]);  // eslint-disable-line

  const filtres = devis.filter(d =>
    !recherche || d.numero?.toLowerCase().includes(recherche.toLowerCase())
              || d.client_nom?.toLowerCase().includes(recherche.toLowerCase())
  );

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-yellow-500/15 border border-yellow-500/30 rounded-lg flex items-center justify-center">
            <FileText size={18} className="text-yellow-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Devis</h1>
            <p className="text-xs text-gray-500">Validité par défaut : 15 jours</p>
          </div>
        </div>
        {canEditBusiness && (
          <button onClick={() => setShowCreer(true)}
            className="flex items-center gap-2 px-3 py-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 text-sm font-medium rounded-lg">
            <Plus size={14} /> Nouveau devis
          </button>
        )}
      </header>

      <div className="bg-gray-900 border border-gray-800 rounded-lg p-3 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs text-gray-400 mb-1">Recherche</label>
          <div className="relative">
            <Search size={12} className="absolute left-2 top-2.5 text-gray-500" />
            <input type="text" value={recherche} onChange={(e) => setRecherche(e.target.value)}
              placeholder="Numéro ou client"
              className="w-full bg-gray-800 border border-gray-700 rounded pl-7 pr-2 py-1.5 text-sm text-white" />
          </div>
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Statut</label>
          <select value={statutFiltre} onChange={(e) => setStatutFiltre(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white">
            <option value="">Tous</option>
            <option value="en_cours">En cours</option>
            <option value="transforme">Transformés</option>
            <option value="expire">Expirés</option>
            <option value="annule">Annulés</option>
          </select>
        </div>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-800/50">
            <tr className="text-left text-xs text-gray-400">
              <th className="px-3 py-2">N°</th>
              <th className="px-3 py-2">Client</th>
              <th className="px-3 py-2">Émis</th>
              <th className="px-3 py-2">Expire</th>
              <th className="px-3 py-2 text-right">Montant</th>
              <th className="px-3 py-2">Statut</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center text-gray-500 py-8">
                <Loader size={20} className="animate-spin mx-auto" />
              </td></tr>
            ) : filtres.length === 0 ? (
              <tr><td colSpan={7} className="text-center text-gray-500 py-8">Aucun devis</td></tr>
            ) : filtres.map(d => {
              const st = STATUTS[d.statut] || STATUTS.en_cours;
              return (
                <tr key={d.id} className="border-t border-gray-800 hover:bg-gray-800/30">
                  <td className="px-3 py-2 font-mono text-xs text-gray-300">{d.numero}</td>
                  <td className="px-3 py-2 text-gray-300">{d.client_nom}</td>
                  <td className="px-3 py-2 text-gray-400 text-xs flex items-center gap-1">
                    <Calendar size={11} /> {fmtDate(d.date_emission)}
                  </td>
                  <td className="px-3 py-2 text-gray-400 text-xs">{fmtDate(d.date_expiration)}</td>
                  <td className="px-3 py-2 text-right text-white tabular-nums">{fmt(d.montant_total)}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded text-xs border ${st.color}`}>{st.label}</span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => setSelectedId(d.id)} className="text-gray-400 hover:text-white">
                      <Eye size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showCreer && <ModalCreer onClose={() => setShowCreer(false)} onSuccess={() => { setShowCreer(false); charger(); }} />}
      {selectedId && <ModalDetail devisId={selectedId} onClose={() => setSelectedId(null)} onChange={charger} />}
    </div>
  );
}
